import {
  replaceSurveySpanWithPlaceholder,
  SURVEY_PLACEHOLDER,
} from './surveyFormatter';

export const getRejectionEmailFormData = ({
  values,
  sendRejectEmail = false,
  processFromUserPayload,
  feSharedEmails,
  users,
}) => {
  const {
    ats_rejection_reason_id,
    selectedTemplate = {},
    schedule_at = null,
  } = values ?? {};
  let { body, subject, cc, bcc, surveyId = null } = values ?? {};
  // senderId and shared_email_id logic
  let ats_shared_email_id = undefined;
  let senderId = undefined;

  if (feSharedEmails) {
    const processedPayload = processFromUserPayload({
      selectedUser:
        values?.rejectEmailConfig?.fromUserObject || values?.fromUserObject,
      payloadKey: 'senderId',
    });
    ats_shared_email_id = processedPayload?.ats_shared_email_id;
    senderId = processedPayload?.senderId;
  } else {
    const from = values?.rejectEmailConfig?.from || values?.from;
    senderId = users?.find((u) => u?.email === from)?.id;
  }

  const rejectEmailConfig = values?.rejectEmailConfig || {};
  // Since we’re using the MenuButton component for survey selection,
  // we need to replace it with the {{survey.link}} placeholder in the body before sending data to the server.
  // Conversely, after receiving data from the server, we’ll replace {{survey.link}} with the MenuButton component
  // (refer to SurveyLinkReplacement.js).
  body = replaceSurveySpanWithPlaceholder(rejectEmailConfig?.body || body);
  subject = rejectEmailConfig?.subject || subject || '';
  cc = rejectEmailConfig?.cc || [];
  bcc = rejectEmailConfig?.bcc || [];
  surveyId = rejectEmailConfig?.survey?.value || null;

  // Last line of defence: an orphan token guarantees a 400 that loses the
  // whole rejection. Dropping it sends a valid email minus the survey link.
  if (!surveyId && body.includes(SURVEY_PLACEHOLDER)) {
    body = body.split(SURVEY_PLACEHOLDER).join('');
  }

  const payloadFD = new FormData();
  payloadFD.append('ats_rejection_reason_id', ats_rejection_reason_id);

  if (sendRejectEmail) {
    payloadFD.append('email[sender_id]', senderId);
    payloadFD.append('email[body]', body);
    payloadFD.append('email[subject]', subject);

    cc?.forEach((id) => payloadFD.append('email[cc][]', id));
    bcc?.forEach((id) => payloadFD.append('email[bcc][]', id));

    let hasTemplateAttachments = false;
    const attachments = values?.attachments || [];
    attachments.forEach((file) => {
      if (file instanceof File) payloadFD.append('email[attachments][]', file);
      if (file?.id) {
        hasTemplateAttachments = true;
        payloadFD.append('email[attachments][]', file?.id);
      }
    });
    if (attachments?.length === 0) payloadFD.append('email[attachments][]', []);
    if (selectedTemplate?.id && hasTemplateAttachments) {
      payloadFD.append('email[ats_comm_template_id]', selectedTemplate?.id);
    }
    if (schedule_at) {
      if (schedule_at?.formattedTime) {
        payloadFD.append('email[scheduled_at]', schedule_at?.formattedTime);
      }
      if (schedule_at?.timezoneValue && schedule_at?.timezoneValue?.key) {
        payloadFD.append('email[timezone]', schedule_at?.timezoneValue?.key);
      }
    }

    // The backend rejects either half alone, so guard both directions here —
    // every reject surface funnels through this builder (CORE-5230).
    if (surveyId && body.includes(SURVEY_PLACEHOLDER)) {
      payloadFD.append('survey[ats_form_template_id]', surveyId);
    }
  }

  // note: this is conditionally added for shared emails
  if (sendRejectEmail && ats_shared_email_id) {
    payloadFD.append('email[ats_shared_email_id]', ats_shared_email_id);
  }

  return payloadFD;
};

export const getEmailFormData = ({
  values,
  senderId,
  ats_shared_email_id = undefined,
  templateReferenceKey = 'ats_comm_template_id',
  ccKey = 'cc[]',
  bccKey = 'bcc[]',
}) => {
  const formData = new FormData();
  const {
    subject,
    body,
    attachments,
    cc,
    bcc,
    appIds,
    selectedTemplate = {},
  } = values;
  let hasTemplateAttachments = false;
  if (senderId) formData.append('sender_id', senderId);
  formData.append('subject', subject);
  formData.append('body', body);
  appIds?.forEach((id) => formData.append('ats_application_ids[]', id));
  cc?.forEach((id) => formData.append(ccKey, id));
  bcc?.forEach((id) => formData.append(bccKey, id));
  attachments.forEach((file) => {
    if (file instanceof File) formData.append('attachments[]', file);
    if (file?.id) {
      hasTemplateAttachments = true;
      formData.append('attachments[]', file?.id);
    }
  });
  if (attachments?.length === 0) formData.append('attachments[]', []);
  if (selectedTemplate?.id && hasTemplateAttachments) {
    formData.append(templateReferenceKey, selectedTemplate?.id);
  }
  //note: this is conditionally added for shared emails
  if (ats_shared_email_id) {
    formData.append('ats_shared_email_id', ats_shared_email_id);
  }
  return formData;
};
